import {
  BffChannel,
  MockActionType,
  MockActionTypeSingle,
} from "../backend-types/channel";
import { BffPollResponse } from "../backend-types/common";
import {
  BffAction,
  BffPaymentEntity,
  BffPaymentEntityType,
  BffPaymentRequest,
  BffPaymentRequestStatus,
  BffPaymentToken,
  BffPaymentTokenStatus,
} from "../backend-types/payment-entity";
import { BffPaymentOptions } from "../backend-types/payment-options";
import { BffSession } from "../backend-types/session";
import { WorldState } from "../public-sdk";
import { assert, randomHexString, randomUUID } from "../utils";

const examplePublicKey =
  "MHYwEAYHKoZIzj0CAQYFK4EEACIDYgAEyCADI5pdf6KmN8+Fxl2ES3yolUKXunNeY3gGScGNEvDcrcHAPKxIInAo5DVnDvTtYtqZvx/bu7HLeBJNMXwHhie/uyNEtT8dSaLc9bd0WSlYdxI+iUsTv2Qu0LiiPrZs";
const exampleSignature =
  "NKf7whM9meUs/eRCvG0oc180MDiyeli3kH6EQ3ZahECHsZQi5G2IpH6vk3cYMtf01Y1L4OBn1SZCOv1kwpjIUet4DJeoTwwq2nM5b+K7rD+/WFTi3AEX4NWJNkKi0a91";

export function makeTestSdkKey() {
  return `session-${randomHexString(32)}-mock-${examplePublicKey}-${exampleSignature}`;
}

export function makeTestPollResponse(
  world: WorldState,
  channel: BffChannel | null,
  result:
    | "SUCCESS"
    | "FAILURE"
    | "PENDING"
    | "PENDING_PAYMENT_ENTITY_ONLY"
    | "SESSION_EXPIRED"
    | "SESSION_COMPLETED",
) {
  const { session, paymentEntity } = world;
  assert(session);
  assert(channel);

  switch (result) {
    // cases affecting session status
    case "PENDING": {
      return makeTestPollResponseForPending(session);
    }
    case "SESSION_EXPIRED": {
      return makeTestPollResponseForSessionExpired(session);
    }
    case "SESSION_COMPLETED": {
      return makeTestPollResponseForSessionCompleted(session);
    }
    // cases affecting paymentEntity status
    case "PENDING_PAYMENT_ENTITY_ONLY": {
      assert(paymentEntity);
      // make the payment entity pending, but keep the session active
      return makeTestPollResponseForPendingPaymentEntityOnly(
        session,
        paymentEntity,
      );
    }
    case "SUCCESS": {
      assert(paymentEntity);
      if (channel._mock_action_type === "PENDING") {
        // channels with mock pending state (like FPX) always go to pending state when success is requested
        return makeTestPollResponseForPending(session);
      } else {
        return makeTestPollResponseForSuccess(session, paymentEntity);
      }
    }
    case "FAILURE": {
      assert(paymentEntity);
      return makeTestPollResponseForFailure(session, paymentEntity);
    }
  }
}

export function makeTestPollResponseForPending(
  session: BffSession,
): BffPollResponse {
  return {
    session: {
      ...session,
      status: "PENDING",
    },
  };
}

export function makeTestPollResponseForSessionExpired(
  session: BffSession,
): BffPollResponse {
  return {
    session: {
      ...session,
      status: "EXPIRED",
    },
  };
}

export function makeTestPollResponseForSessionCompleted(
  session: BffSession,
): BffPollResponse {
  return {
    session: {
      ...session,
      status: "COMPLETED",
    },
  };
}

export function makeTestPollResponseForPendingPaymentEntityOnly(
  session: BffSession,
  paymentEntity: BffPaymentEntity,
): BffPollResponse {
  const paymentRequest =
    paymentEntity.type === BffPaymentEntityType.PaymentRequest
      ? paymentEntity.entity
      : undefined;
  const paymentToken =
    paymentEntity.type === BffPaymentEntityType.PaymentToken
      ? paymentEntity.entity
      : undefined;

  return {
    session,
    payment_request: withPaymentEntityStatus(paymentRequest, "PENDING"),
    payment_token: withPaymentEntityStatus(paymentToken, "PENDING"),
  };
}

export function makeTestPollResponseForSuccess(
  session: BffSession,
  paymentEntity: BffPaymentEntity,
): BffPollResponse {
  const paymentRequest =
    paymentEntity.type === BffPaymentEntityType.PaymentRequest
      ? paymentEntity.entity
      : undefined;
  const paymentToken =
    paymentEntity.type === BffPaymentEntityType.PaymentToken
      ? paymentEntity.entity
      : undefined;

  return {
    session: {
      ...session,
      status: "COMPLETED",
      payment_request_id: paymentRequest?.payment_request_id,
      payment_token_id: paymentToken?.payment_token_id,
    },
    payment_request: withPaymentEntityStatus(paymentRequest, "SUCCEEDED"),
    payment_token: withPaymentEntityStatus(paymentToken, "ACTIVE"),
    succeeded_channel: {
      channel_code: paymentEntity.entity.channel_code,
      logo_url: "https://placehold.co/48",
    },
  };
}

export function makeTestPollResponseForFailure(
  session: BffSession,
  paymentEntity: BffPaymentEntity,
): BffPollResponse {
  const paymentRequest =
    paymentEntity.type === BffPaymentEntityType.PaymentRequest
      ? paymentEntity.entity
      : undefined;
  const paymentToken =
    paymentEntity.type === BffPaymentEntityType.PaymentToken
      ? paymentEntity.entity
      : undefined;

  return {
    session: {
      ...session,
      status: "ACTIVE",
    },
    payment_request: withPaymentEntityStatus(paymentRequest, "FAILED"),
    payment_token: withPaymentEntityStatus(paymentToken, "FAILED"),
  };
}

export function withPaymentEntityStatus<
  T extends BffPaymentRequest | BffPaymentToken | undefined,
>(
  prOrPt: T,
  status: T extends BffPaymentRequest
    ? BffPaymentRequestStatus
    : T extends BffPaymentToken
      ? BffPaymentTokenStatus
      : undefined,
): T {
  if (!prOrPt) return prOrPt;
  return {
    ...prOrPt,
    status: status,
  };
}

export function makeTestPaymentRequest(
  channelCode: string,
  mockActionType: MockActionType | undefined,
): BffPaymentRequest {
  if (mockActionType === "PENDING") {
    return {
      payment_request_id: `pr-${randomUUID()}`,
      status: "PENDING",
      channel_code: channelCode,
      actions: [],
      session_token_request_id: randomUUID(),
    };
  } else if (mockActionType) {
    return {
      payment_request_id: `pr-${randomUUID()}`,
      status: "REQUIRES_ACTION",
      channel_code: channelCode,
      actions: makeMockActions(mockActionType),
      session_token_request_id: randomUUID(),
    };
  } else {
    return {
      payment_request_id: `pr-${randomUUID()}`,
      status: "SUCCEEDED",
      channel_code: channelCode,
      actions: [],
      session_token_request_id: randomUUID(),
    };
  }
}

export function makeTestPaymentToken(
  channelCode: string,
  mockActionType: MockActionType | undefined,
): BffPaymentToken {
  if (mockActionType === "PENDING") {
    return {
      payment_token_id: `pt-${randomUUID()}`,
      status: "PENDING",
      channel_code: channelCode,
      actions: makeMockActions(mockActionType),
      session_token_request_id: randomUUID(),
    };
  } else if (mockActionType) {
    return {
      payment_token_id: `pt-${randomUUID()}`,
      status: "REQUIRES_ACTION",
      channel_code: channelCode,
      actions: makeMockActions(mockActionType),
      session_token_request_id: randomUUID(),
    };
  } else {
    return {
      payment_token_id: `pt-${randomUUID()}`,
      status: "ACTIVE",
      channel_code: channelCode,
      actions: [],
      session_token_request_id: randomUUID(),
    };
  }
}

export function makeMockActions(
  mockActionType: MockActionType | undefined,
): BffAction[] {
  if (!mockActionType) return [];
  const mockActionArray = Array.isArray(mockActionType)
    ? mockActionType
    : [mockActionType];
  const mockActions = mockActionArray
    .map((actionType) => makeOneMockAction(actionType))
    .filter((action) => action !== null);
  return mockActions;
}

export function makeOneMockAction(
  mockActionType: MockActionTypeSingle,
): BffAction | null {
  if (mockActionType === "PENDING") {
    throw new Error("PENDING mock action type should not generate an action");
  }

  switch (mockActionType) {
    case "IFRAME":
      return {
        type: "REDIRECT_CUSTOMER",
        descriptor: "WEB_URL",
        value: "https://example.com/iframe",
        iframe_capable: true,
      };
    case "REDIRECT":
      return {
        type: "REDIRECT_CUSTOMER",
        descriptor: "WEB_URL",
        value: "https://example.com/redirect",
        iframe_capable: false,
      };
    case "PAYLINK":
      return {
        type: "REDIRECT_CUSTOMER",
        descriptor: "WEB_GOOGLE_PAYLINK",
        value: "https://example.com/paylink",
      };
    case "DEEP_LINK":
      return {
        type: "REDIRECT_CUSTOMER",
        descriptor: "DEEPLINK_URL",
        value: "mockapp://mock-deep-link",
      };
    case "PUSH_NOTIFICATION": {
      // push notification actions are represented by setting `{ status: "REQUIRES_ACTION", actions: [] }`
      return null;
    }
    case "QR":
      return {
        type: "PRESENT_TO_CUSTOMER",
        descriptor: "QR_STRING",
        value: "https://example.com/qr-code-data",
        action_title: "Pay with QR Code",
        action_subtitle: "Scan the QR code below",
        action_graphic: "",
        instructions: null,
      };
    case "BARCODE":
      return {
        type: "PRESENT_TO_CUSTOMER",
        descriptor: "PAYMENT_CODE",
        value: "1234567890",
        action_title: "Pay at a Store",
        action_subtitle: "Show this barcode to the cashier",
        action_graphic: "",
        instructions: [
          {
            title: "Mock App 1",
            content: [
              { type: "text", text: "Log in to your mock app" },
              [
                { type: "text", text: "Tap <b>Bills</b>" },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+2",
                  height: 400,
                },
              ],
              [
                { type: "text", text: "Search <b>Mock Biller</b>" },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+3",
                  height: 400,
                },
              ],
              [
                {
                  type: "text",
                  text: "Fill out your <b>account number, payment amount, and mobile number</b>",
                },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+4",
                  height: 400,
                },
              ],
              { type: "text", text: "Tap <b>Continue</b>" },
              [
                {
                  type: "text",
                  text: "Review your details and tap <b>Pay</b>",
                },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+6",
                  height: 400,
                },
              ],
              {
                type: "text",
                text: "Return to this page and click <b>I've made this payment</b> above to confirm your payment.",
              },
            ],
          },
          {
            title: "Mock App 2",
            content: [
              { type: "text", text: "Log in to your mock app" },
              [
                { type: "text", text: "Tap <b>Pay Bills</b>" },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+2",
                  height: 400,
                },
              ],
              [
                { type: "text", text: "Search <b>Mock Biller</b>" },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+3",
                  height: 400,
                },
              ],
              [
                {
                  type: "text",
                  text: "Fill out your <b>payment amount and reference number</b>",
                },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+4",
                  height: 400,
                },
              ],
              [
                { type: "text", text: "Tap <b>Confirm</b>" },
                {
                  type: "image",
                  src: "https://placehold.co/300x400.png?text=Step+5",
                  height: 400,
                },
              ],
              {
                type: "text",
                text: "Return to this page and click <b>I've made this payment</b> above to confirm your payment.",
              },
            ],
          },
        ],
      };
    case "VA":
      return {
        type: "PRESENT_TO_CUSTOMER",
        descriptor: "VIRTUAL_ACCOUNT_NUMBER",
        value: "1234567890",
        action_title: "Pay with Virtual Account",
        action_subtitle:
          "Protect yourself from fraud - ensure all details are correct",
        action_graphic: "",
        instructions: [
          {
            title: "Mobile Banking",
            content: [
              {
                type: "text",
                text: "Open Mobile App",
              },
              {
                type: "text",
                text: "Select <b>Mock VA</b>, then select <b>Transfer</b>",
              },
              {
                type: "text",
                text: "Enter your Virtual Account Number, for example 3816523906568, then press <b>OK</b>",
              },
              {
                type: "text",
                text: "Click on <b>Send</b> button at the top right corner to proceed",
              },
              {
                type: "text",
                text: "Click <b>OK</b> to proceed",
              },
              {
                type: "text",
                text: "Enter your PIN to authorize the transaction",
              },
            ],
          },
          {
            title: "Internet Banking",
            content: [
              {
                type: "text",
                text: "Login to the website",
              },
              {
                type: "text",
                text: "Select <b>Transfer</b>, then select <b>Transfer to Mock VA Virtual Account</b>",
              },
              {
                type: "text",
                text: "Enter the Virtual Account Number, for example 3816523906568",
              },
              {
                type: "text",
                text: "Select <b>Continue</b> to proceed your payment",
              },
            ],
          },
          {
            title: "ATM",
            content: [
              {
                type: "text",
                text: "Insert your ATM card and PIN",
              },
              {
                type: "text",
                text: "Enter your ATM PIN",
              },
              {
                type: "text",
                text: "Select <b>Transfer</b>",
              },
              {
                type: "text",
                text: "Select <b>To Mock VA Virtual Account</b>",
              },
              {
                type: "text",
                text: "Enter Virtual Account Number, for example 3816523906568. Press <b>Correct</b> to proceed",
              },
              {
                type: "text",
                text: "Verify Virtual Account details and then enter amount to be transferred and select <b>Correct</b> to confirm",
              },
              {
                type: "text",
                text: "Confirm your transaction details displayed",
              },
              {
                type: "text",
                text: "Select <b>Yes</b> if the details are correct or <b>No</b> if the details are not correct",
              },
            ],
          },
        ],
      };
  }

  mockActionType satisfies never;
  throw new Error(`Unknown mock action type: ${mockActionType}`);
}

export function makeMockPaymentOptions(
  channelCode: string,
  session: BffSession,
): BffPaymentOptions {
  return {
    channel_code: channelCode,
    country: session.country,
    currency: session.currency,
    amount: session.amount,
    installment_plans: [
      {
        interval: "MONTH",
        interval_count: 1,
        terms: 3,
        installment_amount: Math.floor(session.amount / 3),
        total_amount: session.amount,
        description: `Unused`,
        code: "3M",
        interest_rate: 1,
      },
      {
        interval: "MONTH",
        interval_count: 1,
        terms: 6,
        installment_amount: Math.floor(session.amount / 6),
        total_amount: session.amount,
        description: `Unused`,
        code: "6M",
        interest_rate: 1,
      },
      {
        interval: "MONTH",
        interval_count: 1,
        terms: 9,
        installment_amount: Math.floor(session.amount / 9),
        total_amount: session.amount,
        description: `Unused`,
        interest_rate: 1,
        code: undefined, // this item has no code (like TH installment channels)
      },
    ],
  };
}
